import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import { PanelDataType } from "../constant";
import ChatbotVenueCard from "./ChatbotVenueCard";
import ArrowLeftFilled from "../../../../../Assets/Icons/arrow-left-filled.svg";
import { currencyToNumber, snakeToText } from "../utils";
import Ideas from "../../../../../Assets/Icons/ideas.svg";
import Timeline from "../../../../../Assets/Icons/timeline.svg";
import Money from "../../../../../Assets/Icons/money.svg";
import Place from "../../../../../Assets/Icons/place.svg";
import { useMemo, useRef, useState, useEffect } from "react";
import { CustomAccordion } from "../styles";
import ChatbotAccordionTimeline from "./ChatbotAccordionTimeline";

const ChatbotAccordion = ({
  panel,
  chatId,
  isParsing,
  disableAction,
  parsedSummaryData,
  isExtraRecommendation,
}) => {
  const [budgetTextWidths, setBudgetTextWidths] = useState({});
  const [budgetPercentageWidths, setBudgetPercentageWidths] = useState({});
  const [indicatorWidths, setIndicatorWidths] = useState({});
  const [progressBarWidths, setProgressBarWidths] = useState({});

  const [expanded, setExpanded] = useState(true);

  const textRefs = useRef({});
  const percentageRefs = useRef({});
  const indicatorRefs = useRef({});
  const progressBarRefs = useRef({});

  const addBudgetTextRef = (name, ref) => {
    if (ref) {
      textRefs.current[name] = ref;
    }
  };

  const addBudgetPercentageRef = (name, ref) => {
    if (ref) {
      percentageRefs.current[name] = ref;
    }
  };

  const addIndicatorRef = (name, ref) => {
    if (ref) {
      indicatorRefs.current[name] = ref;
    }
  };

  const addProgressBarRef = (name, ref) => {
    if (ref) {
      progressBarRefs.current[name] = ref;
    }
  };

  useEffect(() => {
    const newTextWidths = {};
    const newPercentageWidths = {};
    const newIndicatorWidths = {};
    const newProgressBarWidths = {};

    Object.entries(textRefs.current).forEach(([key, ref]) => {
      if (ref) {
        newTextWidths[key] = ref.getBoundingClientRect().width;
      }
    });

    Object.entries(percentageRefs.current).forEach(([key, ref]) => {
      if (ref) {
        newPercentageWidths[key] = ref.getBoundingClientRect().width;
      }
    });

    Object.entries(indicatorRefs.current).forEach(([key, ref]) => {
      if (ref) {
        newIndicatorWidths[key] = ref.getBoundingClientRect().width;
      }
    });

    Object.entries(progressBarRefs.current).forEach(([key, ref]) => {
      if (ref) {
        newProgressBarWidths[key] = ref.getBoundingClientRect().width;
      }
    });

    setBudgetTextWidths(newTextWidths);
    setBudgetPercentageWidths(newPercentageWidths);
    setIndicatorWidths(newIndicatorWidths);
    setProgressBarWidths(newProgressBarWidths);
  }, [panel]); // Only re-measure when panel data changes

  const renderAccordionDetail = () => {
    if (
      panel.type === PanelDataType.Timeline ||
      panel.type === PanelDataType.PreWeddingTimeline
    ) {
      const { type, ...timeline } = panel;

      return (
        <ChatbotAccordionTimeline
          isTimeline
          timelineSummaryData={parsedSummaryData["timeline"]}
          chatId={chatId}
          aiTimeline={timeline}
          isParsing={isParsing}
          disableAction={disableAction}
        />
      );
    }

    if (panel.type === PanelDataType.Budget) {
      const { type, ...budgetDetail } = panel;

      const totalBudgetNumber = currencyToNumber(budgetDetail.total_budget);
      let currentTotalBudget = totalBudgetNumber;

      return (
        <>
          {Object.entries(budgetDetail).map(([key, value], index) => {
            const isTotalBudget = key === "total_budget";
            const valueNumber = currencyToNumber(value);
            currentTotalBudget = currentTotalBudget - valueNumber;
            const isOverBudget = currentTotalBudget < 0;

            const percentage = Math.min(
              100,
              (valueNumber / totalBudgetNumber) * 100
            );
            const percentageValue =
              percentage > 1 ? `${percentage?.toFixed(1)}%` : "0%";

            const label = snakeToText(key);

            let valueIndicatorLeft = Math.max(
              budgetTextWidths[key] ? budgetTextWidths[key] + 10 : 0,
              budgetPercentageWidths[key]
                ? budgetPercentageWidths[key] - indicatorWidths?.[key] / 2 || 0
                : 0
            );

            if (
              valueIndicatorLeft + indicatorWidths?.[key] >=
              progressBarWidths[key]
            ) {
              valueIndicatorLeft =
                progressBarWidths[key] - indicatorWidths?.[key];
            }

            if (isTotalBudget) {
              return (
                <div
                  key={index}
                  className={`budget-overview ${
                    isTotalBudget ? "total-budget" : ""
                  }`}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: "#171717",
                      fontWeight: 400,
                      flexBasis: "150px",
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography component="span" fontWeight={400}>
                    {value}
                  </Typography>
                </div>
              );
            }

            return (
              <div key={index} className="budget-overview">
                <Typography
                  component="span"
                  sx={{ color: "#171717", fontWeight: 400 }}
                  ref={(ref) => addBudgetTextRef(key, ref)}
                >
                  {label}
                </Typography>
                <div
                  className="progress-bar"
                  ref={(ref) => addProgressBarRef(key, ref)}
                >
                  <div
                    className="value-indicator"
                    style={{
                      left: valueIndicatorLeft,
                      maxWidth: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      direction: "rtl",
                    }}
                    ref={(ref) => addIndicatorRef(key, ref)}
                  >
                    {value}&nbsp;
                    <span className={`${isOverBudget ? "over-budget" : ""}`}>
                      ({isOverBudget ? "over budget" : percentageValue})
                    </span>
                  </div>
                  <div
                    className={`progress-bar-fill ${
                      isOverBudget ? "over-budget" : ""
                    }`}
                    style={{ width: percentageValue }}
                    ref={(ref) => addBudgetPercentageRef(key, ref)}
                  ></div>
                </div>
              </div>
            );
          })}
        </>
      );
    }

    if (panel.type === PanelDataType.Venues) {
      const { type, ...venues } = panel;

      return Object.entries(venues).map(([key, value]) => (
        <ChatbotVenueCard key={key} data={value} />
      ));
    }

    if (panel.recommendations_type === "place") {
      return panel?.recommendations_items?.map((item, idx) => (
        <ChatbotVenueCard key={idx} data={item} />
      ));
    }

    if (panel.recommendations_type === "other") {
      return panel?.recommendations_items?.map((item, idx) => (
        <div key={idx} className="chatbot-accordion__content-white">
          <Typography
            component="span"
            sx={{
              color: "#171717",
              fontWeight: 400,
              flexBasis: "180px",
              flexGrow: 0,
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            {snakeToText(item.title)}
          </Typography>
          <Typography component="span">{item.value}</Typography>
        </div>
      ));
    }

    return null;
  };

  const title = panel.recommendations_name
    ? panel.recommendations_name
    : panel.type === PanelDataType.Budget
    ? "Estimated Budget"
    : snakeToText(panel.type);

  const icon = useMemo(() => {
    if (panel.type === PanelDataType.Timeline) {
      return Timeline;
    }

    if (panel.type === PanelDataType.Budget) {
      return Money;
    }

    if (panel.type === PanelDataType.Venues) {
      return Place;
    }

    if (panel.type === PanelDataType.Recommendations) {
      return Ideas;
    }
  }, [panel.type]);

  const summary = useMemo(() => {
    const shouldRender = ![
      PanelDataType.Timeline,
      PanelDataType.PreWeddingTimeline,
    ].includes(panel.type);

    const text = isExtraRecommendation
      ? parsedSummaryData?.["extra_recommendations"]?.[
          panel.recommendations_name?.split(" ")?.join("_")
        ] || ""
      : parsedSummaryData?.[panel.type] || "";

    return {
      isRender: shouldRender,
      text: shouldRender ? text : "",
    };
  }, [panel, isExtraRecommendation, parsedSummaryData]);

  return (
    <CustomAccordion
      className="chatbot-accordion"
      defaultExpanded
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary
        expandIcon={
          <img
            src={ArrowLeftFilled}
            alt="arrow-left-filled"
            className="chatbot-accordion__icon--arrow"
          />
        }
      >
        <Box>
          <Typography
            component="span"
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              color: "#171717",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              verticalAlign: "bottom",
            }}
          >
            <img
              src={icon}
              alt="ideas"
              className="chatbot-accordion__icon-prefix"
            />
            {title}
          </Typography>

          {/* timeline value is an object, can't directly render */}
          {summary.isRender && !expanded ? (
            <Typography fontSize="14px">{summary.text}</Typography>
          ) : null}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ borderRadius: "24px" }}>
        {renderAccordionDetail()}
      </AccordionDetails>
    </CustomAccordion>
  );
};
export default ChatbotAccordion;
